import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAmazonProduct } from '../../../../features/amazon/api';
import { getProduct } from '../../../../features/products/api';
import type { Product } from '../../../../features/products/types';
import type { GenerationAsset } from '../../state/useGenerationState';
import {
  STORAGE_KEY_SELECTED_ASIN,
  STORAGE_KEY_PRODUCT_NAME,
  STORAGE_KEY_BRAND_NAME,
} from '../constants';
import { useSelectedProductStore } from '../../../../stores/selectedProductStore';
import type { Mode } from '../types';

interface SelectedProduct {
  id: string;
  brand_id?: string;
  brand_name?: string;
  title?: string;
  main_image_url?: string;
}

type Goal = 'price' | 'branding' | 'awareness' | '';

interface UseProductSelectionParams {
  selectedAssets: GenerationAsset[];
  activeScenarioId: string | null;
  mode: Mode;
}

interface UseProductSelectionReturn {
  asin: string;
  setAsin: React.Dispatch<React.SetStateAction<string>>;
  savedAsin: string | null;

  manualProductId: string | null;
  setManualProductId: React.Dispatch<React.SetStateAction<string | null>>;
  manualProductTitle: string | null;
  setManualProductTitle: React.Dispatch<React.SetStateAction<string | null>>;
  manualProductImageUrl: string | null;
  setManualProductImageUrl: React.Dispatch<React.SetStateAction<string | null>>;

  productName: string;
  setProductName: React.Dispatch<React.SetStateAction<string>>;
  brandName: string;
  setBrandName: React.Dispatch<React.SetStateAction<string>>;
  sellingPoints: string;
  setSellingPoints: React.Dispatch<React.SetStateAction<string>>;
  goal: Goal;
  setGoal: React.Dispatch<React.SetStateAction<Goal>>;
  targetAudience: string;
  setTargetAudience: React.Dispatch<React.SetStateAction<string>>;
  pricePoint: string;
  setPricePoint: React.Dispatch<React.SetStateAction<string>>;
  manualProductPromptDismissed: boolean;
  setManualProductPromptDismissed: React.Dispatch<React.SetStateAction<boolean>>;

  fetchedAsins: Set<string>;
  setFetchedAsins: React.Dispatch<React.SetStateAction<Set<string>>>;
  fetchedProductIds: Set<string>;
  setFetchedProductIds: React.Dispatch<React.SetStateAction<Set<string>>>;

  selectedProduct: SelectedProduct | null | undefined;
  manualProduct: Product | null | undefined;

  productHasBrand: boolean;
  hasRequiredScenarioInput: boolean;
  productSummary: string;
  customProductSummary: string;
}

export type { UseProductSelectionReturn };

export function useProductSelection({
  selectedAssets,
  activeScenarioId,
  mode,
}: UseProductSelectionParams): UseProductSelectionReturn {
  const [asin, setAsin] = useState('');
  const [manualProductId, setManualProductId] = useState<string | null>(null);
  const [manualProductTitle, setManualProductTitle] = useState<string | null>(null);
  const [manualProductImageUrl, setManualProductImageUrl] = useState<string | null>(null);

  // 選択中の商品を ScenarioGenerator の外 (選択素材エリアの「商品画像を復元」) へ公開する
  const setSelectedProduct = useSelectedProductStore((s) => s.setSelectedProduct);
  const clearSelectedProduct = useSelectedProductStore((s) => s.clearSelectedProduct);
  useEffect(() => {
    setSelectedProduct({ asin: asin || null, manualProductId });
  }, [asin, manualProductId, setSelectedProduct]);
  useEffect(() => () => clearSelectedProduct(), [clearSelectedProduct]);

  const [savedAsin, setSavedAsin] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEY_SELECTED_ASIN);
  });

  useEffect(() => {
    if (asin) {
      localStorage.setItem(STORAGE_KEY_SELECTED_ASIN, asin);
      setSavedAsin(asin);
    } else {
      localStorage.removeItem(STORAGE_KEY_SELECTED_ASIN);
      setSavedAsin(null);
    }
  }, [asin]);

  const [productName, setProductName] = useState<string>(
    () => localStorage.getItem(STORAGE_KEY_PRODUCT_NAME) || '',
  );
  const [brandName, setBrandName] = useState<string>(
    () => localStorage.getItem(STORAGE_KEY_BRAND_NAME) || '',
  );
  const [sellingPoints, setSellingPoints] = useState('');
  const [goal, setGoal] = useState<Goal>('');
  const [targetAudience, setTargetAudience] = useState('');
  const [pricePoint, setPricePoint] = useState('');
  const [manualProductPromptDismissed, setManualProductPromptDismissed] = useState(false);

  const [fetchedAsins, setFetchedAsins] = useState<Set<string>>(new Set());
  const [fetchedProductIds, setFetchedProductIds] = useState<Set<string>>(new Set());

  // 商品素材がselectedAssetsから✕で外された場合、UI整合性のためASIN/manualProductIdをクリア
  // （生成ロジック自体はassetsForGenerationからのみ導出するためstateに依存しないが、
  //  UI上の「商品選択中」表示との整合性を保つために必要）
  const prevSelectedAssetsRef = useRef(selectedAssets);
  const prevActiveScenarioIdRef = useRef(activeScenarioId);
  useEffect(() => {
    const prev = prevSelectedAssetsRef.current;
    const prevScenarioId = prevActiveScenarioIdRef.current;
    prevSelectedAssetsRef.current = selectedAssets;
    prevActiveScenarioIdRef.current = activeScenarioId;

    // activeScenarioIdが変わった場合はスコープ切り替えによるリセットなので、
    // 商品素材の「削除」と誤検知しないようスキップする。
    // （新スコープの非同期素材取得が完了する前にASINがクリアされるのを防ぐ）
    if (prevScenarioId !== activeScenarioId) return;

    const removedProductAssets = prev.filter(
      (p) => (p.hasProductLinks || p.asin) && !selectedAssets.some((c) => c.id === p.id),
    );
    if (removedProductAssets.length === 0) return;

    const remainingProductAssets = selectedAssets.filter((a) => a.hasProductLinks || a.asin);

    if (remainingProductAssets.length === 0) {
      if (asin) setAsin('');
      if (manualProductId) {
        setFetchedProductIds((prev) => {
          const next = new Set(prev);
          next.delete(manualProductId);
          return next;
        });
        setManualProductId(null);
        setManualProductTitle(null);
        setManualProductImageUrl(null);
      }
    }
  }, [selectedAssets, asin, manualProductId, activeScenarioId]);

  const { data: selectedProduct } = useQuery<SelectedProduct | null>({
    queryKey: ['product-for-scenario', asin],
    queryFn: async () => {
      if (!asin) return null;
      try {
        const response = await getAmazonProduct(asin);
        return response.product;
      } catch (error) {
        console.error('商品情報の取得に失敗:', error);
        return null;
      }
    },
    enabled: !!asin,
    staleTime: 5 * 60 * 1000,
  });

  const { data: manualProduct } = useQuery<Product | null>({
    queryKey: ['manual-product-for-scenario', manualProductId],
    queryFn: async () => {
      if (!manualProductId) return null;
      try {
        return await getProduct(manualProductId);
      } catch {
        return null;
      }
    },
    enabled: !!manualProductId,
    staleTime: 5 * 60 * 1000,
  });

  const productHasBrand =
    mode === 'asin'
      ? !!selectedProduct?.brand_id
      : mode === 'manual'
        ? !!manualProduct?.brand_id
        : false;

  const hasRequiredScenarioInput = useMemo(() => {
    if (mode === 'asin') return !!asin.trim();
    if (mode === 'manual') return !!manualProductId;
    if (mode === 'custom') return !!productName.trim();
    return false;
  }, [mode, asin, manualProductId, productName]);

  const productSummary = useMemo(() => {
    if (mode === 'asin') {
      return selectedProduct?.title || asin || '';
    }
    if (mode === 'manual') {
      return manualProductTitle || '';
    }
    return '';
  }, [mode, asin, selectedProduct, manualProductTitle]);

  const customProductSummary = useMemo(() => {
    const parts: string[] = [];
    if (productName) parts.push(productName);
    if (brandName) parts.push(brandName);
    if (sellingPoints) {
      const firstPoint = sellingPoints.split('\n')[0].replace(/^[・•\-\*]\s*/, '').trim();
      if (firstPoint) parts.push(firstPoint);
    }
    return parts.join(' / ');
  }, [productName, brandName, sellingPoints]);

  return {
    asin,
    setAsin,
    savedAsin,
    manualProductId,
    setManualProductId,
    manualProductTitle,
    setManualProductTitle,
    manualProductImageUrl,
    setManualProductImageUrl,
    productName,
    setProductName,
    brandName,
    setBrandName,
    sellingPoints,
    setSellingPoints,
    goal,
    setGoal,
    targetAudience,
    setTargetAudience,
    pricePoint,
    setPricePoint,
    manualProductPromptDismissed,
    setManualProductPromptDismissed,
    fetchedAsins,
    setFetchedAsins,
    fetchedProductIds,
    setFetchedProductIds,
    selectedProduct,
    manualProduct,
    productHasBrand,
    hasRequiredScenarioInput,
    productSummary,
    customProductSummary,
  };
}
